import { useRef } from "react";
import { motion, useAnimation, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";

export const EASE = [0.22, 1, 0.36, 1];

// Scroll-linked parallax: element drifts vertically as it crosses the viewport.
// speed > 0 moves slower (lags), < 0 moves faster. Transform-only, reversible.
export const Parallax = ({ children, speed = 0.2, className = "" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [speed * 110, speed * -110]);
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} style={{ y }} className={`will-change-transform ${className}`}>
      {children}
    </motion.div>
  );
};

// Directional slide-in with a hint of rotation, for scroll choreography.
export const SlideIn = ({ children, from = "left", className = "", delay = 0, once = true }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  const x = from === "left" ? -32 : from === "right" ? 32 : 0;
  const rotate = from === "left" ? -1 : from === "right" ? 1 : 0;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, x, rotate }}
      whileInView={{ opacity: 1, x: 0, rotate: 0 }}
      viewport={{ once, margin: "140px" }}
      transition={{ duration: 0.45, delay: Math.min(delay, 0.08), ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

// Cursor-magnetic wrapper for large CTAs and decorative objects.
// Disabled for reduced motion and coarse (touch) pointers.
export const Magnetic = ({ children, strength = 0.25, className = "" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const x = useSpring(0, { stiffness: 170, damping: 15, mass: 0.35 });
  const y = useSpring(0, { stiffness: 170, damping: 15, mass: 0.35 });

  if (reduce) return <div className={className}>{children}</div>;

  const onMove = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    x.set((e.clientX - r.left - r.width / 2) * strength);
    y.set((e.clientY - r.top - r.height / 2) * strength);
  };

  return (
    <motion.div
      ref={ref}
      className={`inline-block will-change-transform ${className}`}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={() => {
        x.set(0);
        y.set(0);
      }}
    >
      {children}
    </motion.div>
  );
};

// Subtle 3D tilt on hover for cards and panels (fine pointers only).
// Toned way down per owner — a gentle lean, never disruptive.
export const Tilt3D = ({ children, className = "", max = 4 }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const rx = useSpring(0, { stiffness: 220, damping: 18, mass: 0.4 });
  const ry = useSpring(0, { stiffness: 220, damping: 18, mass: 0.4 });
  if (reduce) return <div className={className}>{children}</div>;
  const onMove = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * max * 2);
    rx.set(-py * max * 2);
  };
  return (
    <motion.div
      ref={ref}
      className={`will-change-transform ${className}`}
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 900, transformStyle: "preserve-3d" }}
      onMouseMove={onMove}
      onMouseLeave={() => { rx.set(0); ry.set(0); }}
    >
      {children}
    </motion.div>
  );
};

// Jelly wobble on pointer contact — springy squash-and-settle.
export const Jelly = ({ children, className = "" }) => {
  const reduce = useReducedMotion();
  const controls = useAnimation();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      animate={controls}
      onPointerEnter={() =>
        controls.start({
          scale: [1, 1.045, 0.975, 1.015, 1],
          rotate: [0, -1.1, 0.9, -0.4, 0],
          transition: { duration: 0.65, ease: "easeInOut" },
        })
      }
    >
      {children}
    </motion.div>
  );
};

// Floating 3D phrase chip — drifts and sways in 3D like a hologram label.
export const PhraseFloat = ({ text, className = "", dur = 9, delay = 0, tone = "volt" }) => {
  const reduce = useReducedMotion();
  const tones = {
    volt: "border-volt/40 text-volt shadow-[0_0_22px_rgba(233,180,76,0.22)]",
    cyan: "border-cyan/40 text-cyan shadow-[0_0_22px_rgba(79,179,191,0.22)]",
    magenta: "border-magenta/40 text-magenta shadow-[0_0_22px_rgba(124,131,219,0.22)]",
    gold: "border-gold/40 text-gold-soft shadow-[0_0_22px_rgba(201,155,63,0.25)]",
  };
  return (
    <motion.span
      aria-hidden="true"
      className={`pointer-events-none absolute select-none whitespace-nowrap rounded-full border bg-espresso/80 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.24em] backdrop-blur ${tones[tone]} ${className}`}
      style={{ transformPerspective: 700 }}
      animate={
        reduce
          ? {}
          : { y: [0, -10, 0], rotate: [-1.5, 1.5, -1.5], rotateY: [-8, 8, -8] }
      }
      transition={{ duration: dur, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      {text}
    </motion.span>
  );
};

// Liquid sweep reveal: content is uncovered by a soft skewed wipe.
export const LiquidReveal = ({ children, className = "", delay = 0, once = true }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ clipPath: "inset(0 100% 0 0 round 18px)", opacity: 0.4 }}
      whileInView={{ clipPath: "inset(0 0% 0 0 round 0px)", opacity: 1 }}
      viewport={{ once, margin: "140px" }}
      transition={{ duration: 0.5, delay: Math.min(delay, 0.08), ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

// Controlled ripple ring that fires from a chosen point (repair-issue picks).
export const Ripple = ({ trigger, className = "" }) => {
  const reduce = useReducedMotion();
  if (reduce || !trigger) return null;
  return (
    <motion.span
      key={trigger}
      aria-hidden="true"
      className={`pointer-events-none absolute rounded-full border-2 border-cyan ${className}`}
      style={{ width: 24, height: 24, left: "50%", top: "50%", x: "-50%", y: "-50%" }}
      initial={{ scale: 0.2, opacity: 0.85 }}
      animate={{ scale: 9, opacity: 0 }}
      transition={{ duration: 1.1, ease: "easeOut" }}
    />
  );
};

// Tactile press/hover props to spread onto motion-wrapped controls.
export const tactile = {
  whileHover: { y: -2 },
  whileTap: { scale: 0.95 },
  transition: { type: "spring", stiffness: 420, damping: 22 },
};
